import React, { useState, useEffect, useRef, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { paymentApi } from '../../services/paymentService';
import { useInternetStatus } from '../shared/InternetStatus';

// IMPORTANT:
// Don't initialize/load Stripe while offline, otherwise `stripe.js` fails to load and triggers an
// ugly "Uncaught runtime errors" overlay. We create `stripePromise` only when online.

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#000',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true, // Hide postal code from card element, we'll collect it separately
};

const PaymentForm = ({ 
  amount, 
  bookingId, // Legacy support - optional now
  bookingData, // New: booking data for new flow
  paymentType = 'deposit',
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [postalCode, setPostalCode] = useState('');
  
  // Use refs for callbacks to avoid dependency issues
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const metadataRef = useRef(metadata);
  const bookingDataRef = useRef(bookingData);
  
  // Update refs when props change
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    metadataRef.current = metadata;
    bookingDataRef.current = bookingData;
  }, [onError, onSuccess, metadata, bookingData]);
  
  // Create a stable key from bookingData for dependency tracking
  const bookingDataKey = bookingData ? JSON.stringify({
    vendor_id: bookingData.vendor_id,
    service_id: bookingData.service_id,
    package_id: bookingData.package_id,
    event_date: bookingData.event_date,
    start_time: bookingData.start_time,
    total_amount: bookingData.total_amount,
  }) : null;

  // Create payment intent when component mounts
  useEffect(() => {
    // Reset states
    setError(null);
    setClientSecret(null);
    
    const createPaymentIntent = async () => {
      try {
        console.log('Creating payment intent...', { bookingData: bookingDataRef.current, bookingId, amount, paymentType });
        let response;
        
        // Use new flow if bookingData is provided, otherwise use legacy flow
        if (bookingDataRef.current) {
          console.log('Using new flow with bookingData');
          response = await paymentApi.createPaymentIntentWithBooking({
            ...bookingDataRef.current,
            amount,
            currency: 'USD',
            paymentType,
          });
        } else if (bookingId) {
          console.log('Using legacy flow with bookingId');
          // Legacy flow: requires existing booking
          response = await paymentApi.createPaymentIntent({
            bookingId,
            amount,
            currency: 'USD',
            paymentType,
            metadata: metadataRef.current
          });
        } else {
          const errorMsg = 'Either bookingId or bookingData is required';
          console.error('Missing booking data:', { bookingData: bookingDataRef.current, bookingId });
          setError(errorMsg);
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
          return;
        }

        console.log('Payment intent response:', response);
        if (response.success && response.data?.client_secret) {
          console.log('Payment intent created successfully');
          setClientSecret(response.data.client_secret);
        } else {
          const errorMsg = response.message || response.error || 'Failed to initialize payment';
          console.error('Payment intent creation failed:', errorMsg, response);
          setError(errorMsg);
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
        }
      } catch (err) {
        console.error('Payment intent creation error:', err);
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            err.message || 
                            'Failed to create payment intent. Please try again.';
        setError(errorMessage);
        if (onErrorRef.current) {
          onErrorRef.current(errorMessage);
        }
      }
    };

    // Only create payment intent if we have required data and amount
    // Remove !clientSecret check so it recreates when amount/paymentType changes
    if ((bookingId || bookingDataRef.current) && amount > 0) {
      createPaymentIntent();
    } else {
      console.log('Skipping payment intent creation:', { 
        hasBookingId: !!bookingId, 
        hasBookingData: !!bookingDataRef.current, 
        amount
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: metadata is NOT in dependencies - we don't want to recreate payment intent when user types in form fields
    // Only amount, paymentType, bookingId, and bookingData should trigger recreation
  }, [bookingId, bookingDataKey, amount, paymentType]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Validate postal code if provided
      if (!postalCode || postalCode.trim().length < 3) {
        setError('Please enter a valid postal code');
        setProcessing(false);
        if (onErrorRef.current) {
          onErrorRef.current('Please enter a valid postal code');
        }
        return;
      }

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: metadataRef.current?.cardholderName || 'Customer',
              address: {
                postal_code: postalCode.trim(),
              },
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        if (onErrorRef.current) {
          onErrorRef.current(stripeError.message);
        }
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment with backend (bookingId is optional now - booking will be created from metadata)
        try {
          const response = await paymentApi.confirmPayment(
            paymentIntent.id,
            bookingId || null // Optional - booking will be created from payment intent metadata if not provided
          );

          if (response.success) {
            if (onSuccessRef.current) {
              onSuccessRef.current({
                paymentIntentId: paymentIntent.id,
                bookingId: response.data?.booking?.id || bookingId, // Use booking ID from response if available
                amount,
                paymentType,
                ...response.data
              });
            }
          } else {
            setError(response.message || 'Payment confirmation failed');
            if (onErrorRef.current) {
              onErrorRef.current(response.message || 'Payment confirmation failed');
            }
          }
        } catch (err) {
          const errorMessage = err.response?.data?.message || 'Failed to confirm payment';
          setError(errorMessage);
          if (onErrorRef.current) {
            onErrorRef.current(errorMessage);
          }
        }
      } else {
        setError('Payment was not successful');
        if (onErrorRef.current) {
          onErrorRef.current('Payment was not successful');
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred during payment';
      setError(errorMessage);
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (!clientSecret) {
    if (error) {
      return (
        <div className="border-2 border-red-300 rounded-lg p-6 bg-red-50">
          <div className="flex items-center text-red-600 mb-2">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="font-semibold">Failed to create payment intent</span>
          </div>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={() => {
              setError(null);
              // Retry creating payment intent
              const createPaymentIntent = async () => {
                try {
                  const response = await paymentApi.createPaymentIntent({
                    bookingId,
                    amount,
                    currency: 'USD',
                    paymentType,
                    metadata
                  });

                  if (response.success && response.data.client_secret) {
                    setClientSecret(response.data.client_secret);
                  } else {
                    setError(response.message || 'Failed to initialize payment');
                  }
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to create payment intent');
                }
              };
              if (bookingId && amount > 0) {
                createPaymentIntent();
              }
            }}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-600 mr-2" />
        <span className="text-gray-600">Initializing payment...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Card Element */}
      <div>
        <label className="block text-sm font-semibold text-black mb-2">
          Card Information
        </label>
        <div className="border-2 border-gray-300 rounded-lg p-4 focus-within:border-black transition-colors">
          <CardElement options={cardElementOptions} />
        </div>
        {error && (
          <div className="mt-2 flex items-center text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 mr-1" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Cardholder Name */}
      <div>
        <label className="block text-sm font-semibold text-black mb-2">
          Cardholder Name
        </label>
        <input
          type="text"
          placeholder="John Doe"
          value={metadata.cardholderName || ''}
          onChange={(e) => {
            if (metadata.onCardholderNameChange) {
              metadata.onCardholderNameChange(e.target.value);
            }
          }}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
        />
      </div>

      {/* Postal Code */}
      <div>
        <label className="block text-sm font-semibold text-black mb-2">
          Postal Code <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="12345"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          required
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Required for payment processing</p>
      </div>

      {/* Payment Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Payment Amount:</span>
          <span className="text-lg font-bold text-black">
            ${Number(amount).toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Payment Type:</span>
          <span className="text-sm font-semibold text-black capitalize">
            {paymentType === 'full_payment'
              ? 'Full Payment'
              : `Deposit (${bookingData?.payment_percentage ?? 50}%)`}
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Processing Payment...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${Number(amount).toFixed(2)}
          </>
        )}
      </button>

      {/* Security Notice */}
      <p className="text-xs text-gray-500 text-center">
        🔒 Your payment is secured by Stripe. We never store your card details.
      </p>
    </form>
  );
};

const StripePaymentForm = ({ 
  amount, 
  bookingId, // Legacy support - optional now
  bookingData, // New: booking data for new flow
  paymentType = 'deposit',
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const { isOnline, isChecking } = useInternetStatus();
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  // Create a unique key that changes when amount or paymentType changes
  // This forces Elements to remount when payment selection changes
  // Must be called before any early returns (React Hooks rule)
  // Only include stable values (amount, paymentType, bookingId) - NOT bookingData
  // to avoid remounting on every render
  const elementsKey = useMemo(() => {
    return `${bookingId || 'new'}-${amount}-${paymentType}`;
  }, [bookingId, amount, paymentType]);

  const stripePromise = useMemo(() => {
    if (!isOnline || !publishableKey) return null;
    return loadStripe(publishableKey);
  }, [isOnline, publishableKey]);

  if (!publishableKey) {
    return (
      <div className="border-2 border-red-300 rounded-lg p-6 bg-red-50">
        <div className="flex items-center text-red-600 mb-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="font-semibold">Stripe Configuration Error</span>
        </div>
        <p className="text-sm text-red-600">
          Stripe publishable key is not configured. Please check your .env file.
        </p>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="border-2 border-yellow-200 rounded-lg p-6 bg-yellow-50">
        <div className="flex items-center text-yellow-800 mb-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="font-semibold">No internet connection</span>
        </div>
        <p className="text-sm text-yellow-900">
          Please reconnect to the internet to complete your payment{isChecking ? '...' : '.'}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} key={elementsKey}>
      <PaymentForm
        amount={amount}
        bookingId={bookingId}
        bookingData={bookingData}
        paymentType={paymentType}
        onSuccess={onSuccess}
        onError={onError}
        metadata={metadata}
      />
    </Elements>
  );
};

export default StripePaymentForm;

